import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, TrendingDown, Gauge, Timer, HardHat, FileCheck, MessageSquare, Mail, Globe, Heart, Landmark, TrendingUp, AlertTriangle, PieChart, Shield, Zap, Leaf, Truck } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { eventBus } from '@/map/core/events';
import { pauseClock } from '@/map/core/pause';
import { getScenarioById } from '@/map/scenario/definitions/index';

type TelemetryMode = 'water-network' | 'street-works' | 'connected-experiences' | 'capital-investment' | 'street-works-permits' | 'mains-rehab' | 'energy-transition' | 'combined-solution' | 'none';

// ──────────────────────────────────────────────────────────────────────────
// Water Network Telemetry — pressure/flow simulation for burst-main
// ──────────────────────────────────────────────────────────────────────────
function WaterNetworkPanel() {
  const [pressureData, setPressureData] = useState<Array<{ time: number; value: number }>>([]);
  const [flowData, setFlowData] = useState<Array<{ time: number; value: number }>>([]);
  const [currentPressure, setCurrentPressure] = useState(2.1);
  const [currentFlow, setCurrentFlow] = useState(45);
  const [incidentPhase, setIncidentPhase] = useState('monitoring');
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let t = 0;
    const dataInterval = setInterval(() => {
      if (pauseClock.paused) return;
      t += 0.5;
      setElapsed(Math.floor(t));

      let pressure: number;
      if (t < 10) pressure = 2.1 + Math.random() * 0.05;
      else if (t < 15) pressure = 2.1 - ((t - 10) / 5) * 1.8 + Math.random() * 0.1;
      else if (t < 50) pressure = 0.3 + Math.random() * 0.1;
      else if (t < 60) pressure = 0.3 + ((t - 50) / 10) * 1.5 + Math.random() * 0.05;
      else pressure = 1.8 + Math.random() * 0.05;

      let flow: number;
      if (t < 10) flow = 45 + Math.random() * 3;
      else if (t < 15) flow = 45 + ((t - 10) / 5) * 40 + Math.random() * 5;
      else if (t < 50) flow = 80 - ((t - 15) / 35) * 60 + Math.random() * 3;
      else if (t < 60) flow = 20 - ((t - 50) / 10) * 18 + Math.random() * 2;
      else flow = 2 + Math.random() * 1;

      setCurrentPressure(Math.max(0, pressure));
      setCurrentFlow(Math.max(0, flow));
      setPressureData((prev) => [...prev.slice(-40), { time: t, value: Math.max(0, pressure) }]);
      setFlowData((prev) => [...prev.slice(-40), { time: t, value: Math.max(0, flow) }]);

      if (t < 10) setIncidentPhase('monitoring');
      else if (t < 18) setIncidentPhase('detection');
      else if (t < 37) setIncidentPhase('response');
      else if (t < 55) setIncidentPhase('isolation');
      else setIncidentPhase('resolved');
    }, 500);

    return () => clearInterval(dataInterval);
  }, []);

  const phaseColor = (p: string) => {
    switch (p) {
      case 'detection': return 'text-red-400';
      case 'response': return 'text-amber-400';
      case 'isolation': return 'text-amber-400';
      case 'resolved': return 'text-emerald-400';
      default: return 'text-sky-400';
    }
  };

  const phaseBg = (p: string) => {
    switch (p) {
      case 'detection': return 'bg-red-500';
      case 'response': return 'bg-amber-500';
      case 'isolation': return 'bg-amber-500';
      case 'resolved': return 'bg-emerald-500';
      default: return 'bg-sky-500';
    }
  };

  const pressureColor = currentPressure < 0.5 ? '#ef4444' : currentPressure < 1.5 ? '#f59e0b' : '#10b981';
  const flowColor = currentFlow > 60 ? '#ef4444' : currentFlow > 30 ? '#f59e0b' : '#10b981';
  const phases = ['monitoring', 'detection', 'response', 'isolation', 'resolved'];

  const renderSparkline = (data: Array<{ time: number; value: number }>, color: string, max: number) => {
    if (data.length < 2) return null;
    const width = 200;
    const height = 50;
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - (d.value / max) * height;
      return `${x},${y}`;
    }).join(' ');
    const areaPoints = `0,${height} ${points} ${width},${height}`;
    return (
      <svg width={width} height={height} className="w-full h-[50px]">
        <polyline points={areaPoints} fill={`${color}15`} stroke="none" />
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    );
  };

  return (
    <>
      {/* Header */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-2">
          <Activity size={12} className="text-sky-400" />
          <span className="text-[10px] font-semibold text-sky-400 tracking-widest">TELEMETRY</span>
        </div>
      </div>

      {/* Phase */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-[var(--text-secondary)]">PHASE</span>
          <span className={`text-[11px] font-bold ${phaseColor(incidentPhase)}`}>
            {incidentPhase.toUpperCase()}
          </span>
        </div>
        <div className="flex gap-0.5">
          {phases.map((phase) => (
            <div
              key={phase}
              className={`h-1.5 flex-1 rounded-full ${
                phases.indexOf(incidentPhase) >= phases.indexOf(phase) ? phaseBg(phase) : 'bg-[var(--badge-bg)]'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Pressure */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <Gauge size={11} className="text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">PRESSURE</span>
          </div>
          <span className="text-xs font-bold font-mono" style={{ color: pressureColor }}>
            {currentPressure.toFixed(1)} bar
          </span>
        </div>
        <div className="bg-[var(--badge-bg)] rounded-lg p-1 mt-1">
          {renderSparkline(pressureData, pressureColor, 2.5)}
        </div>
      </div>

      {/* Flow Rate */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <TrendingDown size={11} className="text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">FLOW RATE</span>
          </div>
          <span className="text-xs font-bold font-mono" style={{ color: flowColor }}>
            {currentFlow.toFixed(0)} L/s
          </span>
        </div>
        <div className="bg-[var(--badge-bg)] rounded-lg p-1 mt-1">
          {renderSparkline(flowData, flowColor, 100)}
        </div>
      </div>

      {/* Metrics */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <Timer size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">METRICS</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Response Time</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">
              {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Network Load</span>
            <span className="text-[11px] font-bold text-amber-400 font-mono">
              {currentFlow > 50 ? '87%' : currentFlow > 20 ? '62%' : '34%'}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Affected Props</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">
              {currentPressure < 0.5 ? '287' : currentPressure < 1.5 ? '142' : '0'}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Supply Status</span>
            <span className={`text-[11px] font-bold font-mono ${currentPressure < 0.5 ? 'text-red-400' : currentPressure < 1.5 ? 'text-amber-400' : 'text-emerald-400'}`}>
              {currentPressure < 0.5 ? 'CRITICAL' : currentPressure < 1.5 ? 'REDUCED' : 'NORMAL'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Street Works Telemetry — permit, lane rental, gang progress
// ──────────────────────────────────────────────────────────────────────────
function StreetWorksPanel() {
  const [elapsed, setElapsed] = useState(0);
  const [laneRental, setLaneRental] = useState(0);
  const [pipeProgress, setPipeProgress] = useState(0);
  const [gangPhase, setGangPhase] = useState('mobilising');
  const [jeopardy, setJeopardy] = useState(12);

  useEffect(() => {
    let t = 0;
    const interval = setInterval(() => {
      if (pauseClock.paused) return;
      t += 0.5;
      setElapsed(Math.floor(t));

      // Lane rental accrues over time (£800/day = ~£0.55/sec in scenario time compression)
      const rentalRate = 33; // £ per scenario-second for visual drama
      setLaneRental(Math.min(4800, Math.floor(t * rentalRate)));

      // Pipe progress ramps through scenario phases
      if (t < 25) setPipeProgress(0);
      else if (t < 55) setPipeProgress(Math.floor(((t - 25) / 30) * 35));
      else if (t < 90) setPipeProgress(35 + Math.floor(((t - 55) / 35) * 45));
      else if (t < 120) setPipeProgress(80 + Math.floor(((t - 90) / 30) * 20));
      else setPipeProgress(100);

      // Gang phase mirrors acts
      if (t < 25) setGangPhase('mobilising');
      else if (t < 55) setGangPhase('excavating');
      else if (t < 90) setGangPhase('pipe-laying');
      else if (t < 120) setGangPhase('reinstating');
      else setGangPhase('complete');

      // Jeopardy risk rises mid-scenario then resolves
      if (t < 25) setJeopardy(12);
      else if (t < 55) setJeopardy(12 + Math.floor(((t - 25) / 30) * 26));
      else if (t < 70) setJeopardy(38 + Math.floor(((t - 55) / 15) * 24));
      else if (t < 90) setJeopardy(62 - Math.floor(((t - 70) / 20) * 50));
      else setJeopardy(Math.max(0, 12 - Math.floor((t - 90) / 10) * 6));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const phaseColor = (p: string) => {
    switch (p) {
      case 'mobilising': return 'text-sky-400';
      case 'excavating': return 'text-amber-400';
      case 'pipe-laying': return 'text-amber-400';
      case 'reinstating': return 'text-emerald-400';
      case 'complete': return 'text-emerald-400';
      default: return 'text-sky-400';
    }
  };

  const phaseBg = (p: string) => {
    switch (p) {
      case 'mobilising': return 'bg-sky-500';
      case 'excavating': return 'bg-amber-500';
      case 'pipe-laying': return 'bg-amber-500';
      case 'reinstating': return 'bg-emerald-500';
      case 'complete': return 'bg-emerald-500';
      default: return 'bg-slate-500';
    }
  };

  const phases = ['mobilising', 'excavating', 'pipe-laying', 'reinstating', 'complete'];
  const jeopardyColor = jeopardy > 50 ? 'text-red-400' : jeopardy > 25 ? 'text-amber-400' : 'text-emerald-400';
  const progressColor = pipeProgress >= 100 ? '#10b981' : pipeProgress > 60 ? '#f59e0b' : '#0ea5e9';

  return (
    <>
      {/* Header */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-2">
          <HardHat size={12} className="text-amber-400" />
          <span className="text-[10px] font-semibold text-amber-400 tracking-widest">WORKS PROGRESS</span>
        </div>
      </div>

      {/* Gang Phase */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-[var(--text-secondary)]">GANG PHASE</span>
          <span className={`text-[11px] font-bold ${phaseColor(gangPhase)}`}>
            {gangPhase.toUpperCase()}
          </span>
        </div>
        <div className="flex gap-0.5">
          {phases.map((phase) => (
            <div
              key={phase}
              className={`h-1.5 flex-1 rounded-full ${
                phases.indexOf(gangPhase) >= phases.indexOf(phase) ? phaseBg(phase) : 'bg-[var(--badge-bg)]'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Pipe Progress */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[var(--text-secondary)]">PIPE LAID</span>
          <span className="text-xs font-bold font-mono" style={{ color: progressColor }}>
            {Math.floor(pipeProgress * 0.95)}m / 95m
          </span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pipeProgress}%`, backgroundColor: progressColor }}
          />
        </div>
      </div>

      {/* Jeopardy Risk */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[var(--text-secondary)]">JEOPARDY RISK</span>
          <span className={`text-xs font-bold font-mono ${jeopardyColor}`}>
            {jeopardy}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${jeopardy}%`,
              backgroundColor: jeopardy > 50 ? '#ef4444' : jeopardy > 25 ? '#f59e0b' : '#10b981',
            }}
          />
        </div>
      </div>

      {/* Live Stats */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <FileCheck size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">PERMIT STATUS</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Elapsed</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">
              {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Lane Rental</span>
            <span className={`text-[11px] font-bold font-mono ${laneRental > 3000 ? 'text-red-400' : laneRental > 1500 ? 'text-amber-400' : 'text-emerald-400'}`}>
              £{laneRental.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Gangs Active</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">
              {gangPhase === 'complete' ? '0' : gangPhase === 'mobilising' ? '4 mobilising' : '4'}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">S74 Status</span>
            <span className={`text-[11px] font-bold font-mono ${gangPhase === 'complete' ? 'text-emerald-400' : 'text-amber-400'}`}>
              {gangPhase === 'complete' ? 'CLOSED' : 'ACTIVE'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Connected Experiences Telemetry — comms delivery, sentiment, channels
// ──────────────────────────────────────────────────────────────────────────
function ConnectedExperiencesPanel() {
  const [elapsed, setElapsed] = useState(0);
  const [smsSent, setSmsSent] = useState(0);
  const [emailSent, setEmailSent] = useState(0);
  const [portalViews, setPortalViews] = useState(0);
  const [deliveryRate, setDeliveryRate] = useState(0);
  const [sentiment, setSentiment] = useState(0);
  const [phase, setPhase] = useState('preparing');

  useEffect(() => {
    let t = 0;
    const interval = setInterval(() => {
      if (pauseClock.paused) return;
      t += 0.5;
      setElapsed(Math.floor(t));

      // SMS ramps first (0-30s)
      if (t < 8) setSmsSent(0);
      else if (t < 30) setSmsSent(Math.floor(((t - 8) / 22) * 342));
      else setSmsSent(342);

      // Email follows (20-50s)
      if (t < 20) setEmailSent(0);
      else if (t < 50) setEmailSent(Math.floor(((t - 20) / 30) * 342));
      else setEmailSent(342);

      // Portal views accumulate (30-80s)
      if (t < 30) setPortalViews(0);
      else if (t < 80) setPortalViews(Math.floor(((t - 30) / 50) * 218));
      else setPortalViews(218);

      // Delivery rate climbs
      if (t < 8) setDeliveryRate(0);
      else if (t < 50) setDeliveryRate(Math.min(99.7, 60 + ((t - 8) / 42) * 39.7));
      else setDeliveryRate(99.7);

      // Sentiment: starts neutral, dips slightly mid (low response), then resolves high
      if (t < 15) setSentiment(7.0);
      else if (t < 60) setSentiment(7.0 + ((t - 15) / 45) * 1.2);
      else if (t < 80) setSentiment(8.2 + ((t - 60) / 20) * 0.4);
      else if (t < 100) setSentiment(8.6 + ((t - 80) / 20) * 0.8);
      else setSentiment(9.4);

      // Phase progression
      if (t < 8) setPhase('preparing');
      else if (t < 30) setPhase('sms-active');
      else if (t < 50) setPhase('email-active');
      else if (t < 70) setPhase('monitoring');
      else if (t < 100) setPhase('resolving');
      else setPhase('complete');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const phaseLabel = (p: string) => {
    switch (p) {
      case 'preparing': return 'PREPARING';
      case 'sms-active': return 'SMS SENDING';
      case 'email-active': return 'EMAIL SENDING';
      case 'monitoring': return 'MONITORING';
      case 'resolving': return 'RESOLVING';
      case 'complete': return 'COMPLETE';
      default: return 'IDLE';
    }
  };

  const phaseColor = (p: string) => {
    switch (p) {
      case 'preparing': return 'text-sky-400';
      case 'sms-active': return 'text-emerald-400';
      case 'email-active': return 'text-sky-400';
      case 'monitoring': return 'text-amber-400';
      case 'resolving': return 'text-amber-400';
      case 'complete': return 'text-emerald-400';
      default: return 'text-sky-400';
    }
  };

  const phaseBg = (p: string) => {
    switch (p) {
      case 'preparing': return 'bg-sky-500';
      case 'sms-active': return 'bg-emerald-500';
      case 'email-active': return 'bg-sky-500';
      case 'monitoring': return 'bg-amber-500';
      case 'resolving': return 'bg-amber-500';
      case 'complete': return 'bg-emerald-500';
      default: return 'bg-slate-500';
    }
  };

  const phases = ['preparing', 'sms-active', 'email-active', 'monitoring', 'resolving', 'complete'];
  const sentimentColor = sentiment >= 9.0 ? '#10b981' : sentiment >= 8.0 ? '#f59e0b' : '#0ea5e9';

  return (
    <>
      {/* Header */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare size={12} className="text-sky-400" />
          <span className="text-[10px] font-semibold text-sky-400 tracking-widest">COMMS DELIVERY</span>
        </div>
      </div>

      {/* Phase */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-[var(--text-secondary)]">STATUS</span>
          <span className={`text-[11px] font-bold ${phaseColor(phase)}`}>
            {phaseLabel(phase)}
          </span>
        </div>
        <div className="flex gap-0.5">
          {phases.map((p) => (
            <div
              key={p}
              className={`h-1.5 flex-1 rounded-full ${
                phases.indexOf(phase) >= phases.indexOf(p) ? phaseBg(p) : 'bg-[var(--badge-bg)]'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Channel Breakdown */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <Mail size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">CHANNELS</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-[10px] text-[var(--text-secondary)]">SMS</span>
            </div>
            <span className="text-[11px] font-bold text-emerald-400 font-mono">
              {smsSent} / 342
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-sky-500" />
              <span className="text-[10px] text-[var(--text-secondary)]">Email</span>
            </div>
            <span className="text-[11px] font-bold text-sky-400 font-mono">
              {emailSent} / 342
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-[10px] text-[var(--text-secondary)]">Portal</span>
            </div>
            <span className="text-[11px] font-bold text-amber-400 font-mono">
              {portalViews} views
            </span>
          </div>
        </div>
      </div>

      {/* Delivery Rate */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <Globe size={11} className="text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">DELIVERY RATE</span>
          </div>
          <span className="text-xs font-bold font-mono text-emerald-400">
            {deliveryRate.toFixed(1)}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div
            className="h-full rounded-full transition-all duration-500 bg-emerald-500"
            style={{ width: `${deliveryRate}%` }}
          />
        </div>
      </div>

      {/* Sentiment */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <Heart size={11} className="text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">CSAT SENTIMENT</span>
          </div>
          <span className="text-xs font-bold font-mono" style={{ color: sentimentColor }}>
            {sentiment.toFixed(1)} / 10
          </span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${(sentiment / 10) * 100}%`, backgroundColor: sentimentColor }}
          />
        </div>
      </div>

      {/* Metrics */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <Timer size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">METRICS</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Elapsed</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">
              {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Complaints</span>
            <span className="text-[11px] font-bold text-emerald-400 font-mono">0</span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Vulnerable</span>
            <span className="text-[11px] font-bold text-amber-400 font-mono">48 flagged</span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Channels Active</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">
              {phase === 'complete' ? '0' : phase === 'preparing' ? '0' : '3'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Capital Investment Telemetry — budget, confidence, portfolio RAG
// ──────────────────────────────────────────────────────────────────────────
function CapitalInvestmentPanel() {
  const [elapsed, setElapsed] = useState(0);
  const [spend, setSpend] = useState(12);
  const [confidence, setConfidence] = useState(87);
  const [variance, setVariance] = useState(0);
  const [schemesAtRisk, setSchemesAtRisk] = useState(8);
  const [phase, setPhase] = useState('monitoring');

  useEffect(() => {
    let t = 0;
    const interval = setInterval(() => {
      if (pauseClock.paused) return;
      t += 0.5;
      setElapsed(Math.floor(t));

      // Budget spend increases over scenario
      if (t < 20) setSpend(12);
      else if (t < 60) setSpend(12 + ((t - 20) / 40) * 3);
      else setSpend(15);

      // Confidence drops mid-scenario then recovers
      if (t < 20) setConfidence(87);
      else if (t < 40) setConfidence(87 - ((t - 20) / 20) * 25);
      else if (t < 70) setConfidence(62);
      else if (t < 100) setConfidence(62 + ((t - 70) / 30) * 29);
      else setConfidence(91);

      // Variance worsens then recovers
      if (t < 25) setVariance(0);
      else if (t < 50) setVariance(-((t - 25) / 25) * 1500);
      else if (t < 80) setVariance(-1500 + ((t - 50) / 30) * 900);
      else setVariance(-600);

      // Schemes at risk
      if (t < 30) setSchemesAtRisk(8);
      else if (t < 50) setSchemesAtRisk(9);
      else if (t < 90) setSchemesAtRisk(8);
      else setSchemesAtRisk(0);

      // Phase
      if (t < 20) setPhase('monitoring');
      else if (t < 40) setPhase('exception');
      else if (t < 70) setPhase('intervention');
      else if (t < 100) setPhase('recovering');
      else setPhase('resolved');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const phaseColor = (p: string) => {
    switch (p) {
      case 'monitoring': return 'text-sky-400';
      case 'exception': return 'text-red-400';
      case 'intervention': return 'text-amber-400';
      case 'recovering': return 'text-amber-400';
      case 'resolved': return 'text-emerald-400';
      default: return 'text-sky-400';
    }
  };

  const phaseBg = (p: string) => {
    switch (p) {
      case 'monitoring': return 'bg-sky-500';
      case 'exception': return 'bg-red-500';
      case 'intervention': return 'bg-amber-500';
      case 'recovering': return 'bg-amber-500';
      case 'resolved': return 'bg-emerald-500';
      default: return 'bg-slate-500';
    }
  };

  const phases = ['monitoring', 'exception', 'intervention', 'recovering', 'resolved'];
  const confidenceColor = confidence >= 85 ? '#10b981' : confidence >= 70 ? '#f59e0b' : '#ef4444';
  const varianceColor = variance >= 0 ? '#10b981' : variance > -1000 ? '#f59e0b' : '#ef4444';
  const spendPct = (spend / 42) * 100;

  return (
    <>
      {/* Header */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-2">
          <Landmark size={12} className="text-indigo-400" />
          <span className="text-[10px] font-semibold text-indigo-400 tracking-widest">CAPITAL DELIVERY</span>
        </div>
      </div>

      {/* Phase */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-[var(--text-secondary)]">STATUS</span>
          <span className={`text-[11px] font-bold ${phaseColor(phase)}`}>
            {phase.toUpperCase()}
          </span>
        </div>
        <div className="flex gap-0.5">
          {phases.map((p) => (
            <div
              key={p}
              className={`h-1.5 flex-1 rounded-full ${
                phases.indexOf(phase) >= phases.indexOf(p) ? phaseBg(p) : 'bg-[var(--badge-bg)]'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Budget Spend */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <PieChart size={11} className="text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">PROGRAMME SPEND</span>
          </div>
          <span className="text-xs font-bold font-mono text-[var(--text-primary)]">
            £{spend.toFixed(1)}M / £42M
          </span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div
            className="h-full rounded-full transition-all duration-500 bg-indigo-500"
            style={{ width: `${spendPct}%` }}
          />
        </div>
      </div>

      {/* Confidence */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <TrendingUp size={11} className="text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">CONFIDENCE</span>
          </div>
          <span className="text-xs font-bold font-mono" style={{ color: confidenceColor }}>
            {Math.floor(confidence)}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${confidence}%`, backgroundColor: confidenceColor }}
          />
        </div>
      </div>

      {/* Variance */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <TrendingDown size={11} className="text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">FORECAST VARIANCE</span>
          </div>
          <span className="text-xs font-bold font-mono" style={{ color: varianceColor }}>
            {variance >= 0 ? '+' : ''}£{(variance / 1000).toFixed(1)}M
          </span>
        </div>
      </div>

      {/* Metrics */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <AlertTriangle size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">PORTFOLIO</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Total Projects</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">625</span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">At Risk</span>
            <span className={`text-[11px] font-bold font-mono ${schemesAtRisk > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {schemesAtRisk}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Elapsed</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">
              {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Programme RAG</span>
            <span className={`text-[11px] font-bold font-mono ${phase === 'resolved' ? 'text-emerald-400' : phase === 'exception' ? 'text-red-400' : 'text-amber-400'}`}>
              {phase === 'resolved' ? 'GREEN' : phase === 'exception' ? 'RED' : phase === 'monitoring' ? 'GREEN' : 'AMBER'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Street Works Permits Telemetry — multi-utility permits, risk, dispatch
// ──────────────────────────────────────────────────────────────────────────
function StreetWorksPermitsPanel() {
  const [elapsed, setElapsed] = useState(0);
  const [permitsActive, setPermitsActive] = useState(3);
  const [liveRisk, setLiveRisk] = useState(12);
  const [holesOpen, setHolesOpen] = useState(0);
  const [phase, setPhase] = useState('mobilising');

  useEffect(() => {
    let t = 0;
    const interval = setInterval(() => {
      if (pauseClock.paused) return;
      t += 0.5;
      setElapsed(Math.floor(t));

      // Holes open during works
      if (t < 20) setHolesOpen(0);
      else if (t < 30) setHolesOpen(Math.min(6, Math.floor((t - 20) / 2)));
      else if (t < 110) setHolesOpen(6);
      else setHolesOpen(Math.max(0, 6 - Math.floor((t - 110) / 3)));

      // Risk escalates mid-scenario
      if (t < 25) setLiveRisk(12);
      else if (t < 50) setLiveRisk(12 + Math.floor(((t - 25) / 25) * 46));
      else if (t < 80) setLiveRisk(58);
      else if (t < 110) setLiveRisk(58 - Math.floor(((t - 80) / 30) * 46));
      else setLiveRisk(0);

      // Permits close at resolution
      if (t < 110) setPermitsActive(3);
      else if (t < 120) setPermitsActive(2);
      else if (t < 130) setPermitsActive(1);
      else setPermitsActive(0);

      // Phase
      if (t < 20) setPhase('mobilising');
      else if (t < 50) setPhase('works-active');
      else if (t < 80) setPhase('risk-event');
      else if (t < 110) setPhase('reinstating');
      else setPhase('closed');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const phaseColor = (p: string) => {
    switch (p) {
      case 'mobilising': return 'text-sky-400';
      case 'works-active': return 'text-amber-400';
      case 'risk-event': return 'text-red-400';
      case 'reinstating': return 'text-amber-400';
      case 'closed': return 'text-emerald-400';
      default: return 'text-sky-400';
    }
  };

  const phaseBg = (p: string) => {
    switch (p) {
      case 'mobilising': return 'bg-sky-500';
      case 'works-active': return 'bg-amber-500';
      case 'risk-event': return 'bg-red-500';
      case 'reinstating': return 'bg-amber-500';
      case 'closed': return 'bg-emerald-500';
      default: return 'bg-slate-500';
    }
  };

  const phases = ['mobilising', 'works-active', 'risk-event', 'reinstating', 'closed'];
  const riskColor = liveRisk > 50 ? '#ef4444' : liveRisk > 25 ? '#f59e0b' : '#10b981';

  return (
    <>
      {/* Header */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-2">
          <Shield size={12} className="text-orange-400" />
          <span className="text-[10px] font-semibold text-orange-400 tracking-widest">PERMIT LIFECYCLE</span>
        </div>
      </div>

      {/* Phase */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-[var(--text-secondary)]">STATUS</span>
          <span className={`text-[11px] font-bold ${phaseColor(phase)}`}>
            {phase.replace('-', ' ').toUpperCase()}
          </span>
        </div>
        <div className="flex gap-0.5">
          {phases.map((p) => (
            <div
              key={p}
              className={`h-1.5 flex-1 rounded-full ${
                phases.indexOf(phase) >= phases.indexOf(p) ? phaseBg(p) : 'bg-[var(--badge-bg)]'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Live Risk */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <AlertTriangle size={11} className="text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">LIVE RISK</span>
          </div>
          <span className="text-xs font-bold font-mono" style={{ color: riskColor }}>
            {liveRisk}%
          </span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${liveRisk}%`, backgroundColor: riskColor }}
          />
        </div>
      </div>

      {/* Utility Breakdown */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <Zap size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">UTILITIES</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-sky-500" />
              <span className="text-[10px] text-[var(--text-secondary)]">Water Co</span>
            </div>
            <span className={`text-[11px] font-bold font-mono ${phase === 'closed' ? 'text-emerald-400' : phase === 'risk-event' ? 'text-amber-400' : 'text-sky-400'}`}>
              {phase === 'closed' ? 'CLOSED' : phase === 'risk-event' ? 'AT RISK' : 'ACTIVE'}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-[10px] text-[var(--text-secondary)]">Gas Co</span>
            </div>
            <span className={`text-[11px] font-bold font-mono ${phase === 'closed' ? 'text-emerald-400' : phase === 'risk-event' ? 'text-red-400' : 'text-amber-400'}`}>
              {phase === 'closed' ? 'CLOSED' : phase === 'risk-event' ? 'DEFECT' : 'ACTIVE'}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-purple-500" />
              <span className="text-[10px] text-[var(--text-secondary)]">Electric Co</span>
            </div>
            <span className={`text-[11px] font-bold font-mono ${phase === 'closed' ? 'text-emerald-400' : phase === 'risk-event' ? 'text-red-400' : 'text-purple-400'}`}>
              {phase === 'closed' ? 'CLOSED' : phase === 'risk-event' ? 'OVERRUN' : 'ACTIVE'}
            </span>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <Timer size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">METRICS</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Permits Active</span>
            <span className={`text-[11px] font-bold font-mono ${permitsActive === 0 ? 'text-emerald-400' : 'text-[var(--text-primary)]'}`}>
              {permitsActive}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Holes Open</span>
            <span className={`text-[11px] font-bold font-mono ${holesOpen === 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
              {holesOpen}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Elapsed</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">
              {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">S74 Penalty</span>
            <span className={`text-[11px] font-bold font-mono ${phase === 'closed' ? 'text-emerald-400' : phase === 'risk-event' ? 'text-red-400' : 'text-emerald-400'}`}>
              {phase === 'risk-event' ? '£5K/day' : '£0'}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Mains Rehab Telemetry — programme meterage, budget, confidence
// ──────────────────────────────────────────────────────────────────────────
function MainsRehabPanel() {
  const [elapsed, setElapsed] = useState(0);
  const [meterage, setMeterage] = useState(4200);
  const [confidence, setConfidence] = useState(84);
  const [phase, setPhase] = useState('overview');

  useEffect(() => {
    let t = 0;
    const interval = setInterval(() => {
      if (pauseClock.paused) return;
      t += 0.5;
      setElapsed(Math.floor(t));
      if (t < 25) { setMeterage(4200); setConfidence(84); setPhase('overview'); }
      else if (t < 60) { setMeterage(4200 + Math.floor(((t - 25) / 35) * 460)); setConfidence(84); setPhase('pipe-laying'); }
      else if (t < 100) { setMeterage(4660); setConfidence(84 - Math.floor(((t - 60) / 40) * 23)); setPhase('exception'); }
      else if (t < 135) { setMeterage(4660 + Math.floor(((t - 100) / 35) * 440)); setConfidence(61 + Math.floor(((t - 100) / 35) * 13)); setPhase('recovering'); }
      else { setMeterage(5800); setConfidence(92); setPhase('all-green'); }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const confidenceColor = confidence >= 80 ? '#10b981' : confidence >= 65 ? '#f59e0b' : '#ef4444';

  return (
    <>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-2">
          <HardHat size={12} className="text-green-400" />
          <span className="text-[10px] font-semibold text-green-400 tracking-widest">PROGRAMME DELIVERY</span>
        </div>
      </div>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[var(--text-secondary)]">METERAGE</span>
          <span className="text-xs font-bold font-mono text-[var(--text-primary)]">{meterage}m / 12400m</span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div className="h-full rounded-full transition-all duration-500 bg-green-500" style={{ width: `${(meterage / 12400) * 100}%` }} />
        </div>
      </div>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[var(--text-secondary)]">CONFIDENCE</span>
          <span className="text-xs font-bold font-mono" style={{ color: confidenceColor }}>{confidence}%</span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${confidence}%`, backgroundColor: confidenceColor }} />
        </div>
      </div>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <Timer size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">SCHEMES</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Active</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">5</span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Budget</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">£18M</span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Phase</span>
            <span className={`text-[11px] font-bold font-mono ${phase === 'all-green' ? 'text-emerald-400' : phase === 'exception' ? 'text-red-400' : 'text-amber-400'}`}>
              {phase.replace('-', ' ').toUpperCase()}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Elapsed</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">{Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}</span>
          </div>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Energy Transition Telemetry — carbon, CSO level, route efficiency
// ──────────────────────────────────────────────────────────────────────────
function EnergyTransitionPanel() {
  const [elapsed, setElapsed] = useState(0);
  const [carbon, setCarbon] = useState(0);
  const [csoLevel, setCsoLevel] = useState(87);
  const [routeEfficiency, setRouteEfficiency] = useState(0);
  const [phase, setPhase] = useState('overflow');

  useEffect(() => {
    let t = 0;
    const interval = setInterval(() => {
      if (pauseClock.paused) return;
      t += 0.5;
      setElapsed(Math.floor(t));
      if (t < 15) { setCarbon(0); setCsoLevel(87); setRouteEfficiency(0); setPhase('overflow'); }
      else if (t < 50) { setCarbon(0); setCsoLevel(87); setRouteEfficiency(Math.floor(((t - 15) / 35) * 35)); setPhase('comparing'); }
      else if (t < 90) { setCarbon(Math.floor(((t - 50) / 40) * 12)); setCsoLevel(87 - Math.floor(((t - 50) / 40) * 45)); setRouteEfficiency(35); setPhase('en-route'); }
      else { setCarbon(12); setCsoLevel(42); setRouteEfficiency(35); setPhase('resolved'); }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const csoColor = csoLevel > 70 ? '#ef4444' : csoLevel > 55 ? '#f59e0b' : '#10b981';

  return (
    <>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-2">
          <Leaf size={12} className="text-emerald-400" />
          <span className="text-[10px] font-semibold text-emerald-400 tracking-widest">NET-ZERO OPS</span>
        </div>
      </div>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[var(--text-secondary)]">CSO LEVEL</span>
          <span className="text-xs font-bold font-mono" style={{ color: csoColor }}>{csoLevel}%</span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${csoLevel}%`, backgroundColor: csoColor }} />
        </div>
      </div>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[var(--text-secondary)]">CARBON EMITTED</span>
          <span className="text-xs font-bold font-mono text-emerald-400">{carbon.toFixed(1)}kg CO₂</span>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-[var(--text-secondary)]">CARBON SAVED</span>
          <span className="text-xs font-bold font-mono text-emerald-400">{(routeEfficiency > 0 ? 6.5 : 0).toFixed(1)}kg CO₂</span>
        </div>
      </div>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-[var(--text-secondary)]">ROUTE EFFICIENCY</span>
          <span className="text-xs font-bold font-mono text-emerald-400">{routeEfficiency}%</span>
        </div>
        <div className="h-2 rounded-full bg-[var(--badge-bg)] overflow-hidden mt-1">
          <div className="h-full rounded-full transition-all duration-500 bg-emerald-500" style={{ width: `${routeEfficiency}%` }} />
        </div>
      </div>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <Truck size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">TANKER</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Phase</span>
            <span className={`text-[11px] font-bold font-mono ${phase === 'resolved' ? 'text-emerald-400' : phase === 'overflow' ? 'text-red-400' : 'text-amber-400'}`}>
              {phase.toUpperCase()}
            </span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Route</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">{phase === 'comparing' || phase === 'overflow' ? '—' : '8.1km'}</span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Elapsed</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">{Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}</span>
          </div>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Combined Solution Telemetry — follows the shooting script, Scene 1–7.
//
// Operational, not marketing: the current scene, which of the four systems is
// active, the four ORP match dials during Scene 4, and the live status of the
// scheme thread MRP-4471 as it moves portfolio → planned → live → closed.
// Timings track the scenario's scene clock (0/30/60/90/125/160/200s).
// ──────────────────────────────────────────────────────────────────────────
const COMBINED_MODULES = [
  { key: 'capital', label: 'Capital Delivery', sub: 'Invest', icon: Landmark },
  { key: 'orp', label: 'Resource Planning', sub: 'Plan', icon: Activity },
  { key: 'control', label: 'Control Room', sub: 'Deliver', icon: Gauge },
  { key: 'opus', label: 'Opus — Street', sub: 'Street', icon: Truck },
] as const;

const COMBINED_SCENES = [
  'THE PROBLEM',
  'ONE OPERATING LAYER',
  'CAPITAL DELIVERY',
  'RESOURCE PLANNING',
  'THE CONTROL ROOM',
  'STREET & CUSTOMER',
  'COMBINED · CLOSE',
] as const;

function CombinedSolutionPanel() {
  const [elapsed, setElapsed] = useState(0);
  const [scene, setScene] = useState(0);            // 0-based scene index (Scene 1 = 0)
  const [activeModule, setActiveModule] = useState(-1);
  // ORP ride-hailing dials for the winning gang (NV-12), spin up during Scene 4
  const [dials, setDials] = useState({ capacity: 0, availability: 0, performance: 0, cost: 0 });
  const [matched, setMatched] = useState(false);
  // the scheme thread MRP-4471 — its lifecycle status through the scenes
  const [schemeStatus, setSchemeStatus] = useState('PORTFOLIO');

  useEffect(() => {
    let t = 0;
    const interval = setInterval(() => {
      if (pauseClock.paused) return;
      t += 0.5;
      setElapsed(Math.floor(t));

      // Scene boundaries on the script clock: 0/30/60/90/125/160/200s.
      if (t < 30) {                       // Scene 1 — the problem
        setScene(0); setActiveModule(-1); setSchemeStatus('SILOED');
      } else if (t < 60) {                // Scene 2 — one operating layer
        setScene(1); setActiveModule(-1); setSchemeStatus('CONNECTED');
      } else if (t < 90) {                // Scene 3 — Capital Delivery
        setScene(2); setActiveModule(0); setSchemeStatus('GATE PASSED');
      } else if (t < 125) {               // Scene 4 — ORP
        setScene(3); setActiveModule(1); setSchemeStatus('PLANNED · WP-4471');
        const p = Math.min(1, (t - 100) / 18);   // dials spin up as the match locks
        setDials({
          capacity: Math.floor(p * 88),
          availability: Math.floor(p * 95),
          performance: Math.floor(p * 92),
          cost: Math.floor(p * 86),
        });
        if (t > 116) setMatched(true);
      } else if (t < 160) {               // Scene 5 — Control Room
        setScene(4); setActiveModule(2); setSchemeStatus('IN PROGRESS');
        setDials({ capacity: 88, availability: 95, performance: 92, cost: 86 });
        setMatched(true);
      } else if (t < 200) {               // Scene 6 — Opus / street / customer
        setScene(5); setActiveModule(3); setSchemeStatus('ON STREET · PMT-2847');
      } else {                            // Scene 7 — close
        setScene(6); setActiveModule(-1); setSchemeStatus('CLOSED');
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const closed = schemeStatus === 'CLOSED';
  const dialRows: Array<{ label: string; value: number; color: string }> = [
    { label: 'Capacity', value: dials.capacity, color: '#0ea5e9' },
    { label: 'Availability', value: dials.availability, color: '#22c55e' },
    { label: 'Performance', value: dials.performance, color: '#a855f7' },
    { label: 'Cost Fit', value: dials.cost, color: '#f59e0b' },
  ];

  return (
    <>
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-2">
          <Zap size={12} className="text-teal-400" />
          <span className="text-[10px] font-semibold text-teal-400 tracking-widest">COMBINED SOLUTION</span>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[9px] text-teal-300/90 font-mono tracking-wider">SCENE {scene + 1} · {COMBINED_SCENES[scene]}</span>
          <span className="text-[9px] text-[var(--text-muted)] font-mono">{scene + 1}/7</span>
        </div>
        {/* Scene progress pips */}
        <div className="flex gap-1 mt-1.5">
          {COMBINED_SCENES.map((_, i) => (
            <div key={i} className="h-1 flex-1 rounded-full transition-colors duration-500"
              style={{ backgroundColor: i < scene ? '#10b981' : i === scene ? '#2dd4bf' : 'var(--badge-bg)' }} />
          ))}
        </div>
      </div>

      {/* Active system — Invest → Plan → Deliver → Street */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <Activity size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">SYSTEM ACTIVE</span>
        </div>
        <div className="space-y-1.5">
          {COMBINED_MODULES.map((m, i) => {
            const Icon = m.icon;
            const active = i === activeModule;
            const done = (activeModule >= 0 && i < activeModule) || (closed && activeModule === -1);
            return (
              <div
                key={m.key}
                className="flex items-center justify-between rounded px-2 py-1.5 transition-all duration-500"
                style={{
                  backgroundColor: active ? 'rgba(45,212,191,0.14)' : 'var(--badge-bg)',
                  boxShadow: active ? 'inset 0 0 0 1px rgba(45,212,191,0.4)' : 'none',
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon size={12} className={active ? 'text-teal-300' : done ? 'text-emerald-400/70' : 'text-[var(--text-muted)]'} />
                  <span className={`text-[11px] font-medium ${active ? 'text-teal-200' : done ? 'text-emerald-300/70' : 'text-[var(--text-secondary)]'}`}>{m.label}</span>
                </div>
                <span className={`text-[9px] font-mono tracking-widest ${active ? 'text-teal-400' : done ? 'text-emerald-400/60' : 'text-[var(--text-muted)]'}`}>{done && !active ? '✓' : m.sub.toUpperCase()}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ORP dials — the ride-hailing matcher (Scene 4 hero) */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Gauge size={11} className="text-[var(--text-secondary)]" />
            <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">ORP MATCH — NV-12</span>
          </div>
          {matched && <span className="text-[8px] font-bold text-emerald-400 tracking-widest">● MATCHED</span>}
        </div>
        <div className="space-y-2">
          {dialRows.map((d) => (
            <div key={d.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-[var(--text-secondary)]">{d.label}</span>
                <span className="text-[11px] font-bold font-mono" style={{ color: d.color }}>{d.value}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-[var(--badge-bg)] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${d.value}%`, backgroundColor: d.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* The scheme thread — MRP-4471, Elm Avenue, followed through the scenes */}
      <div className="p-3 border-b border-[var(--border)] shrink-0">
        <div className="flex items-center gap-1.5 mb-2">
          <HardHat size={11} className="text-[var(--text-secondary)]" />
          <span className="text-[10px] text-[var(--text-secondary)] tracking-widest">SCHEME MRP-4471</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Elm Avenue</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">180m · £820K</span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Status</span>
            <span className={`text-[11px] font-bold font-mono ${closed ? 'text-emerald-400' : activeModule >= 0 ? 'text-teal-300' : 'text-amber-400'}`}>{schemeStatus}</span>
          </div>
          <div className="flex justify-between items-center bg-[var(--badge-bg)] rounded px-2 py-1">
            <span className="text-[10px] text-[var(--text-secondary)]">Elapsed</span>
            <span className="text-[11px] font-bold text-[var(--text-primary)] font-mono">{Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, '0')}</span>
          </div>
        </div>
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Main wrapper — switches panel content based on scenario telemetry type
// ──────────────────────────────────────────────────────────────────────────
export function ScenarioDataPanel() {
  const activeScenario = useAppStore((s) => s.scenarioState.activeScenario);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<TelemetryMode>('none');

  useEffect(() => {
    if (!activeScenario) {
      setTimeout(() => {
        setVisible(false);
        setMode('none');
      }, 500);
      return;
    }

    // Resolve telemetry from the scenario definition directly (avoids race
    // condition where scenario:start fires before this effect subscribes).
    const def = getScenarioById(activeScenario);
    const telemetry = (def?.telemetry ?? 'none') as TelemetryMode;
    if (telemetry !== 'none') {
      setMode(telemetry);
      setVisible(true);
    } else {
      setMode('none');
      setVisible(false);
    }

    const unsub1 = eventBus.on('scenario:start', (d: any) => {
      const t = (d?.telemetry ?? 'none') as TelemetryMode;
      if (t !== 'none') {
        setMode(t);
        setVisible(true);
      } else {
        setMode('none');
        setVisible(false);
      }
    });

    const unsub2 = eventBus.on('scenario:end', () => {
      setTimeout(() => {
        setVisible(false);
        setMode('none');
      }, 2000);
    });

    return () => { unsub1(); unsub2(); };
  }, [activeScenario]);

  if (!visible || mode === 'none') return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="absolute top-0 right-0 h-full w-[240px] pointer-events-auto z-20"
          initial={{ x: 240, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 240, opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="h-full bg-[var(--panel-bg)] backdrop-blur-2xl border-l border-[var(--border)] flex flex-col">
            {mode === 'water-network' && <WaterNetworkPanel />}
            {mode === 'street-works' && <StreetWorksPanel />}
            {mode === 'connected-experiences' && <ConnectedExperiencesPanel />}
            {mode === 'capital-investment' && <CapitalInvestmentPanel />}
            {mode === 'street-works-permits' && <StreetWorksPermitsPanel />}
            {mode === 'mains-rehab' && <MainsRehabPanel />}
            {mode === 'energy-transition' && <EnergyTransitionPanel />}
            {mode === 'combined-solution' && <CombinedSolutionPanel />}
            <div className="flex-1" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
